import type { KanbanColuna } from '@/features/servicos/shared/kanban-protocolos/kanban-protocolos';
import type { ItemStatus } from '@/features/servicos/shared/models/item-status.model';
import type { AcaoServico } from '@/features/servicos/shared/utils/servicos-acoes.utils';

/**
 * Desfecho de um protocolo dentro do lote.
 *
 * <p>Rodada de correção 1: a união de literais original veio do brief do coordenador, verbatim,
 * contra a convenção do projeto — conjunto fechado de valores usa `enum` com prefixo `E`. Convertido
 * aqui para não deixar a Task 4 espalhar strings soltas pelo template.</p>
 */
export enum EEstadoDoItemDoLote {
    PENDENTE = 'pendente',
    EXECUTANDO = 'executando',
    CONCLUIDO = 'concluido',
    CONCLUIDO_COM_PENDENCIAS = 'concluido_com_pendencias',
    FALHOU = 'falhou'
}

/** Onde a fila está. */
export enum EEstadoDaFila {
    /** Antes do primeiro `iniciar()` desta instância — nunca mais alcançado depois. */
    CONFERINDO = 'conferindo',
    /** Algo está em voo: a fila automática (`iniciar`) OU um `repetir` manual — nunca os dois ao mesmo tempo. */
    RODANDO = 'rodando',
    /** Parada antes de esgotar o lote. Por QUE ela parou é `motivoDaParada` — ver `EMotivoDaParada`. */
    PARADA = 'parada',
    /** A fila automática esgotou todos os itens sem ser interrompida. */
    ENCERRADA = 'encerrada'
}

/**
 * Por que a fila parou. `PARADA` sozinho não basta para a tela nem para as regras: para o analista as
 * causas são coisas muito diferentes — uma ele provocou, a outra significa que a sessão caiu e ele
 * precisa entrar de novo antes de qualquer `Repetir` valer alguma coisa.
 *
 * <p>Publicado pelo serviço (e não deduzido pelo diálogo) porque quem vê o 401 é a fila: um espelho
 * local no componente seria uma segunda verdade sobre o mesmo fato, e divergiria na primeira parada
 * que o diálogo não tivesse provocado.</p>
 */
export enum EMotivoDaParada {
    /** `parar()` explícito: "não toque mais neste lote". É o único motivo que bloqueia `repetir`. */
    PEDIDO_DO_ANALISTA = 'pedido-do-analista',
    /** 401 na finalização. NÃO bloqueia `repetir`: a sessão pode ter voltado, e é para esse cenário que `repetir` existe. */
    SESSAO_EXPIRADA = 'sessao-expirada',
    /** A orquestração da fila quebrou de um jeito que nenhum item previu — rede de segurança do `subscribe`. */
    FALHA_INESPERADA = 'falha-inesperada'
}

/**
 * Um protocolo dentro do lote, com tudo o que ele precisa carregar consigo.
 *
 * <p>O contexto viaja como DADO, e não como leitura de sinal do componente: `gravarMovimento` do
 * quadro lê `movimentoPendente`, `anotacao` e outros sinais singulares, e numa fila isso faria a
 * guarda de identidade matar todos os itens a partir do segundo.</p>
 */
export interface ItemDoLote {
    /** `chaveProtocolo(item)` — ano|numero. */
    chave: string;
    item: ItemStatus;
    estado: EEstadoDoItemDoLote;
    /** Mensagem real do erro daquele item, nunca uma frase fixa. */
    erro: string | null;
    /**
     * O portal já aceitou a finalização deste protocolo: repetir significa gravar o movimento,
     * nunca chamar o portal de novo.
     *
     * <p>Correção final (Crítico 4): PROJEÇÃO da memória do `ConclusoesNoPortalService`, não uma
     * memória própria. Nasce consultando aquele serviço (item preso num lote anterior, ou finalizado
     * pelo fluxo unitário do quadro, já chega aqui como aceito) e é escrito num ponto só, junto do
     * registro lá. Está no item porque a tela precisa do valor linha a linha, dentro de um `computed`
     * que já itera sobre eles — não porque o lote tenha uma verdade paralela sobre o assunto.</p>
     */
    aceitoNoPortal: boolean;
    /**
     * Pendências de cadastro detectadas por serviço (hoje: `pre-medico` e `pre-prestador`; ver
     * `encerramento-lote.service.ts`). Nunca bloqueia a efetivação — só avisa. Rótulos de texto
     * livre (não enum): algumas mensagens carregam nuance própria (ex.: telefone perdido por falta
     * de endereço), não só uma categoria fixa.
     */
    pendencias: string[];
}

/**
 * O que `iniciar` recebe para abrir e processar o lote inteiro. A ação (efetivar OU cancelar) e o
 * motivo são únicos para o lote — não há escolha por protocolo dentro dele.
 *
 * <p>Rodada de correção 1: `coluna` (origem) foi retirada — nada no serviço a lia (Menor apontado na
 * revisão), e reintroduzi-la sem um uso real seria inventar contrato. Quem precisar validar a origem
 * da seleção faz isso ANTES de montar este config.</p>
 */
export interface IniciarLoteConfig {
    itens: ItemStatus[];
    /** Coluna para onde o lote move cada protocolo depois de finalizado no portal. */
    colunaDestino: KanbanColuna;
    acao: AcaoServico;
    motivo?: string;
    idServico: number;
    codigoServico: string;
    /**
     * UF do quadro, usada como fallback quando o protocolo não traz a própria (`ItemStatus.ufCrm`
     * vazio). Vem no config, e não de um sinal de tela, pela mesma razão que todo o resto: a fila
     * processa item a item ao longo do tempo e não pode depender de a tela ainda estar no mesmo
     * serviço/UF quando chegar ao vigésimo.
     *
     * <p>Rodada de correção 2 (Importante 1): sem ela, item sem `ufCrm` ia ao portal com `ufCrm: ''`
     * — e, pior, gravava na memória de aceites sob uma UF que o quadro nunca releria.</p>
     */
    uf: string;
}

/** Contagem do lote para o resumo exibido no diálogo — nunca recomputada a partir de `itens()` sozinho, porque itens removidos por `removerPendente` saem do array. */
export interface ResumoDoLote {
    /** Tamanho original do lote, incluindo o que foi retirado antes de a fila chegar a ele. */
    total: number;
    /** `concluido` + `concluido_com_pendencias`. */
    concluidos: number;
    comPendencias: number;
    /**
     * Falhou SEM o portal ter aceitado nada — nada foi feito lá, e repetir refaz a dupla completa.
     *
     * <p>Rodada de correção 2 (Importante 6): antes somava também os presos, e o princípio "preso não
     * é falha", respeitado linha a linha, era violado justo no agregado que o analista lê primeiro: o
     * topo dizia "2 falharam" enquanto a seção "Falharam:" do texto copiado listava 1. Quem confere o
     * cabeçalho contra a lista conclui que sumiu um protocolo.</p>
     */
    falharam: number;
    /**
     * Finalizados no portal com o movimento do quadro recusado. Número próprio, e não uma fatia de
     * `falharam`, porque a conduta é outra: no portal não há — nem pode haver — nada a refazer, só o
     * movimento. Entra no total processado como qualquer outro desfecho: fora dele, um lote com um
     * preso teria um contador que nunca fecha.
     */
    presos: number;
    /** Retirado da fila por `removerPendente` antes de ser processado. */
    foraDoLote: number;
}
